<template>
  <div class="unscheduled-treatment-pg">
    <!-- Follow-ups redesign (docs/plans/2026-10-02-followups-redesign-plan.md, Phase 3). Super
         Admins get the new branch unless they switch; offices follow STAGE.unscheduledTreatment (src/utils/followups/uiVersion.js);
         the v-else branch below is the classic page, unchanged. The page still owns its data,
         filters, lists and goals — the new branch only binds them to the shared components. -->
    <div v-if="fuUseNewUi" class="fu-ut" data-test="fu-unscheduled-treatment">
      <followups-ui-toggle target="old" page-key="unscheduledTreatment" @toggle="fuSetUi" />
      <followups-page-shell
        title="Unscheduled treatment"
        lede="Treatment planned in Open Dental that hasn’t made it onto the schedule yet."
      >
        <template #actions>
          <followups-filter-button
            label="Your lists"
            icon="list"
            data-test="fu-lists-menu"
            :popover="fuListsPopover"
            @select="fuLoadList"
          />
          <list-download-menu
            queue="unscheduledTreatment"
            :rows="unscheduledTreatmentData || []"
            title="Unscheduled Treatment"
            :ctx="{
              dateFrom: dateFrom,
              dateTo: dateTo,
              dateFormatStr: dateFormatStr,
              currencySymbol: allSettings && allSettings.GENERAL_CURRENCY_SYMBOL ? allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue : '$',
              remindersPaidFor: false,
              siteName: getSite(siteID) ? (getSite(siteID).SiteName || getSite(siteID).Name || '') : '',
            }"
          />
        </template>
        <template #tabs>
          <followups-tabs :tabs="fuTabs" active="unscheduledTreatment" />
        </template>

        <ErrorDisplay/>
        <followups-summary-cards
          :due-today="fuCounts ? fuCounts.dueToday : 0"
          :past-due="fuCounts ? fuCounts.pastDue : 0"
          :due-active="fuView === 'now'"
          :goals="fuGoals"
          @show-due="fuView = 'now'"
          @show-all="fuView = 'all'"
        />
        <followups-queue
          queue="unscheduledTreatment"
          mode="page"
          :rows="unscheduledTreatmentData || []"
          :selected="selectedPatients"
          :busy="busy"
          :view.sync="fuView"
          @counts="fuCounts = $event"
          @toggle-row="row => updateSelectedPatients(selectedPatients, row)"
          @toggle-all="fuToggleAll"
          @call-goals-updated="fuCallGoalsUpdated"
          @clear-selection="clearSelection"
          @add-to-existing-list="fuOpenListDialog('add')"
          @create-new-list="fuOpenListDialog('create')"
        >
          <template #switches>
            <followups-switch
              v-model="withAptsInNext60"
              label="Nothing booked in the next 60 days"
              aria-describedby="fu_help_next60"
              data-test="fu-no-appt"
            />
            <span id="fu_help_next60" class="fu-ut__help" tabindex="0" aria-label="About this filter">
              <fu-icon name="info" :size="16" />
            </span>
            <b-popover target="fu_help_next60" triggers="hover focus" placement="bottom">
              <template v-slot:title>Filtering Patients</template>
              <div class="help_popover">
                <h4 class="fw-semi-bold">If the Filter is "On"</h4>
                <p>The list will not show patients who have appointments within the next 60 days.</p>
                <hr/>
                <h4 class="fw-semi-bold">If the Filter is "Off"</h4>
                <p>All patients with unscheduled treatment on their treatment plan are shown.</p>
              </div>
            </b-popover>
          </template>
          <template #filters>
            <followups-filter-button
              icon="cal"
              :label="fuDateLabel"
              data-test="fu-filter-date"
              :popover="fuDatePopover"
              @apply="fuApplyDates"
            />
            <followups-filter-button
              icon="users"
              :label="fuProvidersLabel"
              data-test="fu-filter-providers"
              :popover="fuProvidersPopover"
              @apply="fuApplyProviders"
            />
          </template>
          <template #filters-end>
            <followups-filter-button
              icon="sliders"
              variant="ghost"
              :chevron="false"
              :active="fuMoreCount > 0"
              :label="fuMoreCount ? `More filters · ${fuMoreCount}` : 'More filters'"
              data-test="fu-more-filters"
              @click="fuMoreOpen = true"
            />
          </template>
        </followups-queue>
      </followups-page-shell>

      <followups-more-filters
        v-if="fuMoreOpen"
        :sections="fuMoreSections"
        :value="fuMoreValue"
        @apply="fuApplyMore"
        @close="fuMoreOpen = false"
      />
      <followups-list-dialog
        v-if="fuDialog"
        :kind="fuDialog"
        :lists="fuDialogLists"
        :patients="fuDialogPatients"
        :busy="fuSaving"
        @save="fuSaveList"
        @remove-patient="id => removeSelectedPatient(selectedPatients, id)"
        @close="fuDialog = null"
      />
    </div>
    <template v-else>
    <followups-ui-toggle target="new" page-key="unscheduledTreatment" @toggle="fuSetUi" />
    <h2 class="page-title mb-4">
      Unscheduled Treatment
    </h2>
    <UnscheduledTreatmentSkeleton v-if="busy" />
    <b-row v-show="!busy">
      <!-- Filter Panel -->
      <b-col xs="12" xl="6" class="px-xl-3 mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-filter section-icon"></i>
              Filter Patients
            </h5>
          </div>
          <div class="filter-content">
            <b-row>
              <b-col md="6">
                <div class="filter-group">
                  <label class="filter-label">Tx Plan Date From</label>
                  <DatePicker
                    lang="en"
                    :placeholder="datepickerText"
                    input-class="form-control"
                    v-model="dateFrom"
                    :format="dateFormatStr"
                    class="w-100"
                  >
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>

                <div class="filter-group">
                  <label class="filter-label">To</label>
                  <DatePicker
                    lang="en"
                    :placeholder="datepickerText"
                    input-class="form-control"
                    v-model="dateTo"
                    :format="dateFormatStr"
                    class="w-100"
                  >
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>

                <div class="filter-group">
                  <label class="filter-label">Codes</label>
                  <b-form-group class="abc-radio mb-2">
                    <b-form-radio-group
                      id="include_exclude_codes"
                      v-model="includeOrExclude"
                      :options="includeExcludeOptions"
                      name="radio-options-code"
                    ></b-form-radio-group>
                  </b-form-group>

                  <div v-if="utCustomCategories" class="mb-2">
                    <label class="filter-label">Custom Tags</label>
                    <div v-b-tooltip.hover="utSelectedCustomCategories && utSelectedCustomCategories.length > 2 ? utSelectedCustomCategories.join(', ') : ''">
                    <multiselect
                      v-if="utCustomCategories"
                      v-model="utSelectedCustomCategories"
                      :options="utCustomCategories"
                      :multiple="true"
                      :taggable="true"
                      :limit="2"
                      :limit-text="count => `+${count} more`"
                    >
                      <template slot="tag" slot-scope="props">
                        <span class="custom__tag">
                          <span>{{props.option}}</span>
                          <i tabindex="1" class="multiselect__tag-icon" @click="removeCustomTag(props.option)"></i>
                        </span>
                      </template>
                    </multiselect>
                    </div>
                  </div>

                  <div class="d-flex align-items-end">
                    <div class="flex-grow-1">
                      <label class="filter-label">Proc Codes</label>
                      <div v-b-tooltip.hover="selectedCodes && selectedCodes.length > 2 ? selectedCodes.map(c => c.ProcCode).join(', ') : ''">
                      <multiselect
                        v-if="getCodeOptions"
                        v-model="selectedCodes"
                        :options="getCodeOptions"
                        :multiple="true"
                        :taggable="false"
                        :show-labels="false"
                        :custom-label="codeWithDesc"
                        :limit="2"
                        :limit-text="count => `+${count} more`"
                        label="ProcCode"
                        track-by="ProcCode"
                        aria-placeholder="Add Options"
                        @search-change="searchCodes"
                      >
                        <template slot="tag" slot-scope="props">
                          <span class="custom__tag">
                            <span>{{props.option.ProcCode}}</span>
                            <i tabindex="1" class="multiselect__tag-icon" @click="removeCode(props.option)"></i>
                          </span>
                        </template>
                        <template slot="option" slot-scope="props">
                          <div class="option__desc">
                            <div class="option__title d-flex">
                              <span class="option-code">{{ codeWithDesc(props.option) }}</span>
                            </div>
                          </div>
                        </template>
                      </multiselect>
                      </div>
                    </div>
                    <span v-if="allSettings && (allSettings.UNSCHEDULED_TREATMENT_INCLUDE_CODES || allSettings.UNSCHEDULED_TREATMENT_EXCLUDE_CODES)" class="ml-2 mb-2">
                      <a href="#" @click="loadSavedProcCodes"><i id="load-proc-codes" class="glyphicon glyphicon-upload" slot="upload-icon" /></a>
                      <b-tooltip class="tool_tip" target="load-proc-codes" placement="right" triggers="hover">Load saved Proc Codes</b-tooltip>
                    </span>
                  </div>
                </div>

                <div class="filter-group">
                  <label class="filter-label">Providers</label>
                  <div v-b-tooltip.hover="selectedProvider && selectedProvider.length > 2 ? selectedProvider.map(p => providerCustomLabel(p)).join(', ') : ''">
                  <multiselect
                    v-if="getProviderOptions"
                    v-model="selectedProvider"
                    :options="getProviderOptions"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :custom-label="providerCustomLabel"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                    label="FName"
                    track-by="ProvNum"
                    placeholder="Add Options"
                  >
                    <template slot="singleLabel" slot-scope="props">
                      <span class="custom__tag">
                        <img class="option__image" src="../../assets/icons-user-40.png" alt="Team member image" />
                        <span>{{ providerCustomLabel(props.option) }}</span>
                        <i tabindex="1" class="multiselect__tag-icon" @click="removeProvider(props.option)"></i>
                      </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <img class="option__image" src="../../assets/icons-user-40.png" alt="Team member image" />
                      <div class="option__desc">
                        <span class="option__title">{{ providerCustomLabel(props.option) }}</span>
                      </div>
                    </template>
                  </multiselect>
                  </div>
                </div>
              </b-col>

              <b-col md="6">
                <div class="filter-group">
                  <label class="filter-label">Filter by Plans</label>
                  <b-form-group class="abc-radio mb-2">
                    <b-form-radio-group
                      id="include_exclude_plans"
                      v-model="includeOrExcludePlan"
                      :options="includeExcludeOptions"
                      name="radio-options-plans"
                    ></b-form-radio-group>
                  </b-form-group>

                  <div v-b-tooltip.hover="selectedPlans && selectedPlans.length > 2 ? selectedPlans.map(p => p.CarrierName).join(', ') : ''">
                  <multiselect
                    v-if="getPlanOptions"
                    v-model="selectedPlans"
                    :options="getPlanOptions"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :custom-label="planOptionsWithCount"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                    label="CarrierName"
                    track-by="CarrierNum"
                    @search-change="searchPlans"
                    aria-placeholder="Add Options"
                  >
                    <template slot="tag" slot-scope="props">
                      <span class="custom__tag">
                        <span>{{props.option.CarrierName}}</span>
                        <i tabindex="1" class="multiselect__tag-icon" @click="removePlan(props.option)"></i>
                      </span>
                    </template>
                    <template slot="option" slot-scope="props">
                      <div class="option__desc">
                        <div class="option__title d-flex">
                          <span class="option-code">{{planOptionsWithCount(props.option)}}</span>
                        </div>
                      </div>
                    </template>
                  </multiselect>
                  </div>
                </div>

                <div class="filter-group">
                  <label class="filter-label">Treatment Priorities</label>
                  <b-form-group class="abc-radio mb-2">
                    <b-form-radio-group
                      id="include_exclude_tx"
                      v-model="includeOrExcludePriority"
                      :options="includeExcludeOptions"
                      name="radio-options-tx"
                    ></b-form-radio-group>
                  </b-form-group>

                  <div v-b-tooltip.hover="selectedTxPriority && selectedTxPriority.length > 2 ? selectedTxPriority.map(p => p.ItemName).join(', ') : ''">
                  <multiselect
                    v-if="getTxPriorityOptions"
                    v-model="selectedTxPriority"
                    :options="getTxPriorityOptions"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    :limit="2"
                    :limit-text="count => `+${count} more`"
                    label="ItemName"
                    track-by="DefNum"
                    aria-placeholder="Add Options"
                  ></multiselect>
                  </div>
                </div>

                <div class="filter-group">
                  <label class="filter-label">Amount Remaining Over</label>
                  <b-input-group id="prepended-field">
                    <b-input-group-text slot="prepend" class="bg-transparent" style="border: 1px solid #e2e8f0;">
                      <i class="fa fa-dollar" />
                    </b-input-group-text>
                    <b-form-input v-model="amountRemainingOver" style="border: 1px solid #e2e8f0;"></b-form-input>
                  </b-input-group>
                </div>

                <button class="apply-filters-btn mt-3" @click="applyFilter">
                  <i class="fa fa-search"></i>
                  Apply Filters
                </button>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>

      <!-- Lists Panel -->
      <b-col xs="12" md="6" xl="3" class="px-xl-3 mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-th-list section-icon"></i>
              Your Lists
            </h5>
          </div>
          <div class="filter-content">
            <div v-if="this.getLists && this.getLists.length > 0">
              <div class="filter-group">
                <label class="filter-label">Unscheduled Treatment Lists</label>
                <b-dropdown id="simple-select" :text="selectedListName" variant="default" class="w-100" boundary="window">
                  <b-dropdown-item-button v-for="list in this.getLists" :key="list.ListID" @click="loadList(list.ListID, list.Name)">
                    <i class="glyphicon glyphicon-th-list text-secondary mr-xs mb-xs" /> {{list.Name}}
                  </b-dropdown-item-button>
                </b-dropdown>
              </div>

              <!-- Recent Lists -->
              <div class="recent-lists mt-3" v-if="recentLists.length > 0">
                <label class="filter-label">Recently Created</label>
                <div class="recent-list-items">
                  <div
                    v-for="list in recentLists"
                    :key="'recent-' + list.ListID"
                    class="recent-list-item"
                    @click="loadList(list.ListID, list.Name)"
                  >
                    <i class="fa fa-clock-o text-muted"></i>
                    <span class="list-name">{{ list.Name }}</span>
                  </div>
                </div>
              </div>

              <!-- List Stats -->
              <div class="list-stats mt-3">
                <div class="stat-item">
                  <i class="fa fa-folder-open text-muted"></i>
                  <span class="stat-text">{{ getLists.length }} list{{ getLists.length !== 1 ? 's' : '' }} created</span>
                </div>
              </div>
            </div>

            <div v-if="!this.getLists || this.getLists.length === 0" class="empty-lists-state">
              <div class="empty-icon">
                <i class="fa fa-list-alt"></i>
              </div>
              <h6 class="empty-title">Create your first List</h6>
              <p class="empty-description">Organize patients into focused groups for better follow-up.</p>

              <div class="list-ideas">
                <p class="ideas-label">Ideas to get started:</p>
                <ul class="ideas-list">
                  <li><i class="fa fa-check text-success"></i> High-value treatments over $1,000</li>
                  <li><i class="fa fa-check text-success"></i> Patients by specific provider</li>
                  <li><i class="fa fa-check text-success"></i> Pending crowns or implants</li>
                </ul>
              </div>

              <p class="empty-hint">
                <i class="fa fa-hand-pointer-o"></i>
                Select patients below and click "Create New List"
              </p>
            </div>
          </div>
        </div>
      </b-col>

      <!-- Goals Panel -->
      <b-col xs="12" md="6" xl="3" class="px-xl-3 mb-3">
        <div class="modern-filter-panel h-100">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-bullseye section-icon"></i>
              Call Goals
            </h5>
          </div>
          <div class="filter-content" v-if="allSettings">
            <div class="goal-item mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="goal-label"><strong>Daily</strong> goal</span>
                <span class="goal-count">{{this.dailyAttempts}} / {{this.allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue}}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div role="progressbar" :aria-valuenow="dailyPercent" class="progress-bar bg-success" :style="dailyStyle"></div>
              </div>
            </div>

            <div class="goal-item mb-3">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="goal-label"><strong>Weekly</strong> goal</span>
                <span class="goal-count">{{this.weeklyAttempts}} / {{this.allSettings.UNSCHEDULED_TREATMENT_WEEKLY_CALLS_GOAL.PropertyValue}}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div role="progressbar" :aria-valuenow="weeklyPercent" class="progress-bar bg-info" :style="weeklyStyle"></div>
              </div>
            </div>

            <div class="goal-item">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="goal-label"><strong>Monthly</strong> goal</span>
                <span class="goal-count">{{this.monthlyAttempts}} / {{this.allSettings.UNSCHEDULED_TREATMENT_MONTHLY_CALLS_GOAL.PropertyValue}}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div role="progressbar" :aria-valuenow="monthlyPercent" class="progress-bar bg-primary" :style="monthlyStyle"></div>
              </div>
            </div>

            <!-- Motivational Message -->
            <div class="goal-motivation mt-3">
              <div v-if="dailyPercent >= 100" class="motivation-badge success">
                <i class="fa fa-trophy"></i>
                <span>Daily goal achieved!</span>
              </div>
              <div v-else-if="dailyPercent >= 75" class="motivation-badge almost">
                <i class="fa fa-fire"></i>
                <span>Almost there! {{ allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue - dailyAttempts }} more to hit daily goal</span>
              </div>
              <div v-else class="motivation-badge info">
                <i class="fa fa-phone"></i>
                <span>{{ allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue - dailyAttempts }} calls to reach today's goal</span>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col class="px-xl-3">
        <div class="modern-table-panel">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-list section-icon"></i>
              Unscheduled Treatment Patients
            </h5>
            <div class="panel-controls mt-2">
              <div class="d-flex align-items-center">
                <b-form-checkbox
                  id="withAptsInNext60"
                  v-model="withAptsInNext60"
                  name="checkbox-1"
                  switch
                  size="lg"
                  class="mr-2"
                />
                <span class="filter-label-text">Only show patients without appointments in next 60 days</span>
                <span class="ml-2" id="help_frequency"><i class="fa fa-question-circle text-warning"></i></span>
                <b-popover target="help_frequency" triggers="hover" placement="top">
                  <template v-slot:title>Filtering Patients</template>
                  <div class="help_popover">
                    <h4 class="fw-semi-bold">If the Filter is "On"</h4>
                    <p>The list will not show patients who have appointments within the next 60 days.</p>
                    <hr/>
                    <h4 class="fw-semi-bold">If the Filter is "Off"</h4>
                    <p>All patients with unscheduled treatment on their treatment plan are shown.</p>
                  </div>
                </b-popover>
              </div>
              <div class="mt-2">
                <list-download-menu
                  queue="unscheduledTreatment"
                  :rows="unscheduledTreatmentData || []"
                  title="Unscheduled Treatment"
                  :ctx="{
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                    dateFormatStr: dateFormatStr,
                    currencySymbol: allSettings && allSettings.GENERAL_CURRENCY_SYMBOL ? allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue : '$',
                    remindersPaidFor: false,
                    siteName: getSite(siteID) ? (getSite(siteID).SiteName || getSite(siteID).Name || '') : '',
                  }"
                />
              </div>
            </div>
          </div>
          <div class="table-content">
            <ErrorDisplay/>
            <AdminTable
              v-if="unscheduledTreatmentData"
              :data="unscheduledTreatmentData"
              :columns="columns"
              :options="options"
              :extra="teamioTableExtraParams"
              :selectedPatients="selectedPatients"
              :showBulkActions="true"
              :tableTitle="'Unscheduled Treatment Patients'"
              @call-goals-updated="callGoalsUpdated"
              @clear-selection="clearSelection"
              @add-to-existing-list="addToExistingList"
              @create-new-list="createNewList"
            />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Modal -->
    <b-modal
      ref="create-list"
      id="create-list"
      title="Create List: Unscheduled Treatment"
      body-class="bg-white mb-0"
      scrollable
    >
      <b-row>
        <b-col>
          <b-form-group
            horizontal
            label="List Title:"
            label-for="normal-field"
            :label-cols="2"
            breakpoint="md"
            class="mt-2"
          >
            <b-form-input
              v-model="listTitle"
              type="text"
              id
              style="border: 1px solid #e8e8e8;"
              placeholder="My Sample Unscheduled List"
              @input="resetError"
            />
          </b-form-group>

          <b-form-group
            horizontal
            label="Description:"
            label-for="normal-field"
            :label-cols="3"
            breakpoint="md"
            class="mt-2"
          >
            <textarea v-model="listDescription" name placeholder="Describe your list here (optional)" class="form-control"></textarea>
          </b-form-group>

          <!--
          <div class="routine-form">
            <label>Assign to:</label>
            <multiselect
              v-if="this.activeEmployees"
              v-model="listAssignedTo"
              :options="this.activeEmployees"
              :multiple="false"
              :taggable="false"
              :show-labels="false"
              :custom-label="employeesCustomLabel"
              label="Name"
              track-by="Name"
              aria-placeholder
              disabled
            >
              <template slot="singleLabel" slot-scope="props">
                <span class="custom__tag">
                  <img
                    class="option__image"
                    src="../../assets/icons-user-40.png"
                    alt="Team member image"
                  />
                  <span>{{ props.option.Name }}</span>
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <img
                  class="option__image"
                  src="../../assets/icons-user-40.png"
                  alt="Team member image"
                />
                <div class="option__desc">
                  <span class="option__title">{{ props.option.Name }}</span>
                </div>
              </template>
            </multiselect>
          </div>
          end of assigned to for list -->

          <h6 style="margin-top: 30px;">
            <span class="font-weight-bold mr-1">{{selectedPatients.length}}</span>
            <span class="fw-semi-bold text-success">Patients</span> selected
          </h6>

          <div class="table-responsive patient-list-table mt-1" v-if="selectedPatients.length > 0">
            <v-client-table :data="selectedPatients" :columns="patientColumns" :options="patientOptions">
              <div slot="h__TeamioPatientID"></div>
              <div slot="h__firstName">First Name</div>
              <div slot="h__lastName">Last Name</div>
              <div slot="TeamioPatientID" slot-scope="props">
                <span class="fa fa-trash-o delete_icon text-danger" @click="removeSelectedPatient(selectedPatients, props.row.PatNum)"></span>
              </div>
            </v-client-table>
          </div>
        </b-col>
      </b-row>

      <div slot="modal-footer" class="w-100">
        <b-button
          variant="danger"
          size="sm"
          class="float-right"
          @click="hideModal('create-list')"
        >Cancel</b-button>
        <b-button
          variant="success"
          size="sm"
          class="float-right mr-3"
          @click="saveList"
          :disabled="this.loading || this.somePatientsBelongsToList"
        >Create & Save List</b-button>
      </div>
      <ErrorDisplay/>
    </b-modal>

    <b-modal
      ref="add-to-existing-list"
      id="add-to-existing-list"
      title="Add to Existing List: Unscheduled Treatment"
      body-class="bg-white mb-0"
    >
      <b-row>
        <b-col>
          <label class="mt-2 mb-1">Select List to add patients to:</label>
          <div class="routine-form mt-1">
            <multiselect
              v-model="selectedList"
              :options="getLists"
              :multiple="false"
              :taggable="false"
              :show-labels="false"
              label="Name"
              track-by="Name"
              aria-placeholder
              @select="resetError"
            >
              <template slot="singleLabel" slot-scope="props">
                <span class="custom__tag">
                  <!-- <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image"/> -->
                  <span>{{ props.option.Name }}</span>
                  <!-- <i tabindex="1" class="multiselect__tag-icon" @click="removeMember(props.option)"></i> -->
                  <!-- <span class="custom__remove" @click="removeMember(props.option)">X</span> -->
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <!-- <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image" /> -->
                <div class="option__desc">
                  <span class="option__title">{{ props.option.Name }}</span>
                </div>
              </template>
            </multiselect>
          </div>
          <!-- routine-form -->

          <h6 style="margin-top: 30px;">
            <span class="font-weight-bold mr-1">{{selectedPatients.length}}</span>
            <span class="fw-semi-bold text-success">Patients</span> selected
          </h6>

          <div class="table-responsive patient-list-table mt-1">
            
             <v-client-table :data="selectedPatients" :columns="patientColumns" :options="patientOptions">
              <div slot="h__PatNum">#</div>
              <div slot="h__firstName">First Name</div>
              <div slot="h__lastName">Last Name</div>
              <div slot="PatNum" slot-scope="props">
                <span class="fa fa-trash-o delete_icon text-danger" @click="removeSelectedPatient(selectedPatients, props.row.PatNum)"></span>
              </div>
            </v-client-table>
          </div>
        </b-col>
      </b-row>

      <div slot="modal-footer" class="w-100">
        <b-button
          variant="danger"
          size="sm"
          class="float-right"
          @click="selectedList=[]; hideModal('add-to-existing-list')"
        >Cancel</b-button>
        <b-button
          variant="success"
          size="sm"
          class="float-right mr-3"
          @click="saveList($event, selectedList.ListID)"
          :disabled="loading || somePatientsBelongsToList"
        >Add to List & Save</b-button>
      </div>
      <ErrorDisplay/>
    </b-modal>

    </template>
    <!-- The new branch's ONE slide-out outlet (the follow-up drawer). The legacy branch's outlet
         lives inside AdminTable; v-if / v-else keeps the two from ever being mounted together. -->
    <slideout-panel v-if="fuUseNewUi"></slideout-panel>
  </div>
  <!-- unscheduled-treatment-pg -->
</template>

<script>
/* eslint-disable */

import Vue from "vue";
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import Widget from "@/components/Widget/Widget";
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import $ from "jquery";
import CommonQueries from '../../sql/queries';
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import mixin from '../../mixin.js';
import AdminTable from '@/pages/Admin/AdminTable';
import UnscheduledTreatmentSkeleton from './UnscheduledTreatmentSkeleton.vue';
// Follow-ups redesign (Phase 3) — the new branch of the template. See fuUseNewUi.
import FollowupsQueue from '@/components/Followups/FollowupsQueue.vue';
import FollowupsPageShell from '@/components/Followups/FollowupsPageShell.vue';
import FollowupsTabs from '@/components/Followups/FollowupsTabs.vue';
import FollowupsSummaryCards from '@/components/Followups/FollowupsSummaryCards.vue';
import FollowupsFilterButton from '@/components/Followups/FollowupsFilterButton.vue';
import FollowupsMoreFilters from '@/components/Followups/FollowupsMoreFilters.vue';
import FollowupsListDialog from '@/components/Followups/FollowupsListDialog.vue';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import FuIcon from '@/components/Followups/FuIcon.vue';
import ListDownloadMenu from '@/components/Followups/ListDownloadMenu.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import { visibleTabs, sortListsNewestFirst } from '@/utils/followups/pageChrome.js';

// A procedure code typed into More filters (one not in the code list) goes into the query's
// `procCodes` as '<code>' — so only letters, digits, '.', '_' and '-', at most 15 (OD's ProcCode).
const FU_TYPED_CODE = /^[A-Za-z0-9._-]{1,15}$/;


export default {
  name: 'UnscheduledTreatment',
  components: {
    DatePicker, Multiselect, Widget, ErrorDisplay, AdminTable, UnscheduledTreatmentSkeleton,
    FollowupsQueue, FollowupsPageShell, FollowupsTabs, FollowupsSummaryCards, FollowupsFilterButton,
    FollowupsMoreFilters, FollowupsListDialog, FollowupsSwitch, FollowupsUiToggle, FuIcon,
    ListDownloadMenu,
  },
  props: ['lists', 'masterList'],
  mixins: [mixin],

  created() {
    /*
      The very first time page is loaded, default provider is not available. So we'll watch it and call init then. However, on the 2nd load (if you move away from this page
      and come back), the variable IS avialable and we can just call init here.
    */
    if(this.getDefaultProvider) {
      //console.log(`it is available so will call init now`);
      this.init();
    }
  },

  mounted() {
    //this.initCharts();
  },

  data() {
    return {
      results: [],
      utSelectedCustomCategories: [],
      withAptsInNext60: false,
      teamioTableExtraParams: {
        detailPageName: 'unscheduledPatientDetails',
      },
      unscheduledTreatmentStatusColors: {
        'Archived': 'grey',
        'Call Back, Not Ready': 'primary',
        'Couldn\'t Leave Voicemail': 'pink',
        'Left Msg on Ans. Mach' : 'f87979',
        'No Answer': 'purple',
        'Not Called': 'orange',
        'Patient Asked Not To Be Called': 'danger',
        'Scheduled': 'success',
        'Patient Will Call Us': 'FF6384',
      },
      dateFrom: '',
      dateTo: '',
      datepickerText: "",
      amountRemainingOver: 0,
      color: {
        hex: "#cccccc"
      },
      isPickerActive: false,
      includeOrExclude: '',
      includeOrExcludePlan: '',
      includeOrExcludePriority: '',
      includeExcludeOptions:[
        {text:'Include', value: 'INCLUDE'},
        {text:'Exclude', value: 'EXCLUDE'}
      ],      
      selectedCodes: [],
      selectedPlans: [],
      selectedTxPriority: [],
      selectedPatients: [],
      selectedProvider: [],
      selectedList : 'Select',
      selectedListName: "Select",
      selectedList: [],
      columns: ['id', 'Patient', 'dateTxPlanned', 'Carrier', 'insAmts','txPlanAmtInfo','Status','FollowUpActionPlan', 'Attempts'],

      options: {
          skin:'table',
          sortByColumn: true,
          sortable: ['Patient', 'dateTxPlanned', 'Carrier', 'txPlanAmtInfo', 'Status', 'Attempts'],
          perPage: 50,
          pagination: { chunk: 10, dropdown: false },
          filterByColumn: true,               // https://matanya.gitbook.io/vue-tables-2/list-filters
          filterable: ['Patient', 'Carrier', 'Status', 'Attempts'],
          texts: {
            filter: 'Filter by Patient or Carrier'
            //count:' '
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          },
          sortIcon: {
            base: "fa",
            is: "fa-sort",
            up: "fa-sort-asc",  
            down: "fa-sort-desc"
          },
          customSorting: {
            txPlanAmtInfo: function (ascending) {
                  return function (a, b) {                      
                      if (ascending)
                          return Number(a.officeFeeTotal) - Number(b.officeFeeTotal);
                      return Number(b.officeFeeTotal) - Number(a.officeFeeTotal);
                  }
              }
          }          
          // columnsClasses: {id:'padding-top-0'}
      },
      // isMainChecked: false,
        patientColumns:['PatNum','firstName', 'lastName'],
        patientOptions: {
          filterable: false,
          skin:'table',
          sortable: [],
          perPage: 10,
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          columnsClasses: {PatNum:'text-center'}
        },
      busy: false,
      listTitle: '',
      listDescription: '',
      // Follow-ups redesign (new branch only) — UI state; the data and filters above stay the truth.
      fuUiVersion: 0,      // bumped by the Classic / New view links so fuUseNewUi re-reads storage
      fuView: 'all',       // the table's view chip (:view.sync), set by the "Due now" card
      fuCounts: null,      // the queue's chip counts + { dueToday, pastDue }
      fuMoreOpen: false,
      fuDialog: null,      // 'create' | 'add' | null
      fuSaving: false,
    };
  },
  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('teamio', ['runQuery']),
    ...mapActions('lists',['createList']),
    ...mapActions('customer', ['getSites', 'getEmployees', 'getSettings']),
    ...mapMutations('teamio', ['emptyResults']),
    ...mapMutations('lists', ['setLists', 'setMasterList']),

    // ---- Follow-ups redesign (new branch only). Each one hands off to the page's EXISTING
    // ---- models and methods (applyFilter → init, saveList, loadList); none re-implements them.

    /** The "Classic view" / "New view" links: a Super Admin re-renders in place, an office user reloads (uiSwitch.js). */
    fuSetUi(target) {
      if (switchUi('unscheduledTreatment', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID }) === 'rerender') this.fuUiVersion += 1;
    },

    /** dateFrom / dateTo hold a Date (picker), 'MM/DD/YY' (init) or nothing → 'YYYY-MM-DD'. */
    fuYmd(v) {
      if (!v) return '';
      const m = typeof v === 'string' && /^\d{2}\/\d{2}\/\d{2}$/.test(v) ? moment(v, 'MM/DD/YY') : moment(v);
      return m.isValid() ? m.format('YYYY-MM-DD') : '';
    },

    fuApplyDates({ from, to }) {
      this.dateFrom = moment(from, 'YYYY-MM-DD').toDate();
      this.dateTo = moment(to, 'YYYY-MM-DD').toDate();
      this.applyFilter();
    },

    fuProviderLabel(p) {
      const name = [p.FName, p.MI, p.LName, p.Suffix].filter(x => x && String(x).trim()).join(' ');
      return p.Abbr ? `${name} (${p.Abbr})` : name;
    },

    fuApplyProviders(provNums) {
      const pool = this.fuProviderPool;
      this.selectedProvider = provNums
        .map(n => pool.find(p => String(p.ProvNum) === String(n)))
        .filter(Boolean);
      this.applyFilter();
    },

    /** `list` plus the entries of `extra` it lacks, matched on `field` (a pick list's pool). */
    fuPool(list, extra, field) {
      const pool = [];
      const seen = {};
      (list || []).concat(extra || []).forEach((x) => {
        const v = x ? x[field] : undefined;
        if (v === undefined || v === null || v === '' || seen[String(v)]) return;
        seen[String(v)] = true;
        pool.push(x);
      });
      return pool;
    },

    /** The pool entries whose `field` is one of `values`, in that order. */
    fuPick(pool, field, values) {
      return (values || []).map(v => pool.find(p => String(p[field]) === String(v))).filter(Boolean);
    },

    fuCodeLabel(c) {
      return c.Descript ? `${c.ProcCode} · ${c.Descript}` : String(c.ProcCode);
    },

    /**
     * A picked code → the object the old multiselect holds: the one already selected, else the
     * pool's (code list, custom category, saved codes), else — a code typed in More filters — a
     * bare { ProcCode, Descript: '' }, which init() sends like any other.
     */
    fuCodeObject(code) {
      const same = c => c && String(c.ProcCode) === String(code);
      return (this.selectedCodes || []).find(same) || this.fuCodePool.find(same) || { ProcCode: String(code), Descript: '' };
    },

    /**
     * More filters → the old panel's models, then applyFilter() (its Apply Filters button). A pick
     * list only needs Include / Exclude once something is picked — an empty one leaves the page's
     * mode as it was (init() refuses a pick with no mode; the sheet always has one).
     */
    fuApplyMore(v) {
      const codes = (v.codes && v.codes.selected) || [];
      this.selectedCodes = codes.map(c => this.fuCodeObject(c));
      if (this.selectedCodes.length) this.includeOrExclude = v.codes.mode;
      this.selectedPlans = this.fuPick(this.fuPlanPool, 'CarrierNum', v.plans && v.plans.selected);
      if (this.selectedPlans.length) this.includeOrExcludePlan = v.plans.mode;
      this.selectedTxPriority = this.fuPick(this.fuPriorityPool, 'DefNum', v.priorities && v.priorities.selected);
      if (this.selectedTxPriority.length) this.includeOrExcludePriority = v.priorities.mode;
      const amount = v.amount ? v.amount.text : '';
      this.amountRemainingOver = amount === '' ? 0 : amount; // 0 is the page's own "no filter"
      this.fuMoreOpen = false;
      this.applyFilter();
    },

    fuLoadList(list) {
      this.loadList(list.id, list.name);
    },

    fuToggleAll({ rows, checked }) {
      if (checked) this.chooseAll(rows, this.selectedPatients);
      else this.chooseNone(this.selectedPatients);
    },

    // The bulk bar's two buttons. The dialog itself shows the "already on a list" warning and
    // keeps Save disabled (the old modals' Save was disabled by somePatientsBelongsToList too).
    fuOpenListDialog(kind) {
      this.resetError();
      this.fuDialog = kind;
    },

    async fuSaveList({ kind, name, description, listId }) {
      if (this.fuSaving) return;
      this.fuSaving = true;
      try {
        if (kind === 'create') {
          this.listTitle = name;
          this.listDescription = description;
          await this.saveList(null);
        } else {
          this.selectedList = (this.getLists || []).find(l => l.ListID === listId) || [];
          await this.saveList(null, listId);
        }
        // On success saveList reloads the page; on a failure the error shows on the page.
        if (this.errored) this.fuDialog = null;
      } finally {
        this.fuSaving = false;
      }
    },

    /**
     * A follow-up was logged in the drawer: +1 on each goal card. Not the page's callGoalsUpdated()
     * (kept as is for the old branch): that one writes result sets 5–7, but this page's attempts
     * are sets 6–8 (5 is its lists), so its goals never move. This bumps the sets the goal
     * computeds (dailyAttempts / weeklyAttempts / monthlyAttempts) read.
     */
    fuCallGoalsUpdated() {
      const d = this.dataToDisplay;
      [[6, 'dailyAttempts'], [7, 'weeklyAttempts'], [8, 'monthlyAttempts']].forEach(([i, k]) => {
        const row = d && d[i] && d[i][0];
        if (row) Vue.set(row, k, (Number(row[k]) || 0) + 1);
      });
    },

    async init() {
      this.busy = true;
      //let dateFrom = this.dateFrom ? moment(this.dateFrom).format('YYYY-MM-DD') :  moment().subtract(6, 'months').format('YYYY-MM-DD');
      //let dateTo = this.dateTo ? moment(this.dateTo).format('YYYY-MM-DD') :  moment().format('YYYY-MM-DD');
      let dateFrom = this.dateFrom ? moment(this.dateFrom).format('MM/DD/YY') :  moment().subtract(6, 'months').format('MM/DD/YY');
      let dateTo = this.dateTo ? moment(this.dateTo).format('MM/DD/YY') :  moment().format('MM/DD/YY');
      //console.log(`dateFrom`, dateFrom);
      //console.log(`dateTo`, dateTo);
      this.dateFrom = dateFrom;
      this.dateTo = dateTo;
      let procCodes; 
      let providers = [];
      let insPlans;
      let amount;
      let priorities = [];

      this.resetError();

      if (!this.selectedProvider || this.selectedProvider.length === 0) {
        this.selectedProvider = this.getDefaultProvider;
      }
      for (const p of this.selectedProvider) {
        providers.push(p.ProvNum);
      }

      if(providers.length === 0) {        
        this.$toast.error('Please select one or more Providers');
        this.busy = false;
        return;
      }

      if(this.selectedCodes.length > 0) {
        if(this.includeOrExclude === "") {          
          this.$toast.error('Please select either INCLUDE or EXCLUDE for Codes option before submitting');
          this.busy = false;
          return false;
        }
        procCodes = _.map(this.selectedCodes, function(item) {
          return `'${item.ProcCode}'`;
        }).join(',');
        //console.log('procCodes:' + procCodes);
      }

      if(this.selectedPlans.length > 0) {
        
        if(this.includeOrExcludePlan === "") {          
          this.$toast.error('Please select either INCLUDE or EXCLUDE for Plan option before submitting');
          this.busy = false;
          return false;
        }

        insPlans = _.map(this.selectedPlans, function(item) {
          return `${item.CarrierNum}`;
        }).join(',');
        //console.log('Plans:' + insPlans);
      }

      if(this.selectedTxPriority.length > 0) {
        if(this.includeOrExcludePriority === "") {
          this.$toast.error('Please select either INCLUDE or EXCLUDE for Treatment Priority option before submitting');
          this.busy = false;
          return false;
        }

        priorities = _.map(this.selectedTxPriority, function(item) {
          return `${item.DefNum}`;
        }).join(',');
      }

      if(this.amountRemainingOver)
        amount = this.amountRemainingOver;

      let query = {
        type: 'UNSCHEDULED_TREATMENT',
        siteID: this.siteID,
        listID: 0,
        dateFrom: moment(dateFrom).format('YYYY-MM-DD'),  // have to do this here coz backedn expects it in this format..don't change this
        dateTo: moment(dateTo).format('YYYY-MM-DD'), // have to do this here coz backedn expects it in this format..don't change this
        procCodes: procCodes,
        includeOrExclude: this.includeOrExclude,
        includeOrExcludePlan: this.includeOrExcludePlan,
        includeOrExcludePriority: this.includeOrExcludePriority,
        insPlans: insPlans,
        priorities: priorities,
        amount: amount,
        _date: moment().format('YYYY-MM-DD'),
        country: localStorage.getItem('country') || 'USA',
        providers: providers
      };

      this.emptyResults();
      try {
        this.results = await this.runQuery(query);
        this.busy = false;
      } catch (err) {
        this.busy = false;
      }
    },

    loadSavedProcCodes() {
      if(this.includeOrExclude === 'INCLUDE') {
        let codes = this.allSettings.UNSCHEDULED_TREATMENT_INCLUDE_CODES ? JSON.parse(this.allSettings.UNSCHEDULED_TREATMENT_INCLUDE_CODES.PropertyValue) : null;
        //console.log('codes', codes);
        if(codes && codes.length > 0) {
          this.selectedCodes = codes;
        }
      } else if(this.includeOrExclude === 'EXCLUDE') {        
        let codes = this.allSettings.UNSCHEDULED_TREATMENT_EXCLUDE_CODES ? JSON.parse(this.allSettings.UNSCHEDULED_TREATMENT_EXCLUDE_CODES.PropertyValue) : null;
        //console.log('codes', codes);
        if(codes && codes.length > 0) {
          this.selectedCodes = codes;
        }
      } else {
        this.$toast.error("Please select INCLUDE or EXCLUDE before loading the codes");        
        return false;
      }
    },

    searchPlans(s) { 
      s = s.trim();      
      if(s !== '') {
        if (s.indexOf('*') > -1) {
          let str = s.substring(0,s.length-1);
          //console.log('str', str);          
          for(let option of this.getPlanOptions) {
            //console.dir(option);
            if(option.CarrierName.toUpperCase().startsWith(str.toUpperCase())) {
              //console.dir(option);
              this.selectedPlans.push(option);
            }
          }
        }
      }
    },

    searchCodes(s) { 
      s = s.trim();      
      if(s !== '') {
        if (s.indexOf('*') > -1) {
          let str = s.substring(0,s.length-1);
          //console.log('str', str);          
          for(let option of this.getCodeOptions) {
            //console.dir(option);
            if(option.ProcCode.toUpperCase().startsWith(str.toUpperCase())) {
              //console.dir(option);
              this.selectedCodes.push(option);
            }
          }
        }
      }
    },

    isDueToday(d) {
      if(d === null || d === '')
        return false;
      else
        return moment(d).isSame(moment(), 'day');
    },

    isEmpty(obj) {
      for(var key in obj) {
          if(obj.hasOwnProperty(key))
              return false;
      }
      return true;
    },

    applyFilter() {
      //console.log('applying filter');
      let diff = moment(this.dateTo).diff(moment(this.dateFrom), 'months', true);
      //console.log(`diff: ${diff}`);
      if(diff > 24) {
        this.$toast.error('You can only filter with maximum of 24 months difference between the dates. Please try again.');        
        return false;  
      }
      this.init();
    },

    loadList(listID, name) {
      this.$router.push({
        name: 'unscheduledList',
        params: {
          listID: listID,
        }
      });
    },

    async saveList(event, listID=null) {
      let payload;

      if(listID === null) {
        if(this.listTitle === '') {
          this.$toast.error('Please provide a list name');          
          return false;     
        }

        payload = {
          Name : this.listTitle,
          Description: this.listDescription,
          CreatedAt: moment().format('YYYY-MM-DDTHH:mm:ss'),
          CreatedByEmpID: this.userID,
          //AssignedToEmpID: this.listAssignedTo.EmployeeID,
          AssignedToEmpID: this.userID,   //changed this when we did multiple emp set up
          TaskTypeID: 4, // UNSCHEDULED TREATMENT
          SiteID: this.siteID,
          FilterDates: this.dateFrom && this.dateTo ? 'From ' + moment(this.dateFrom).format('MM-DD-YYYY') + ' to ' + moment(this.dateTo).format('MM-DD-YYYY') : '',
          FilterProvider: this.selectedProvider.length > 0 ? `${this.selectedProvider.FName} ${this.selectedProvider.LName}` : '',
          FilterCarriers: this.selectedPlans.length > 0 ?_.map(this.selectedPlans, 'CarrierName').join(',') : '',
          FilterCodes: this.selectedCodes.length > 0 ?_.map(this.selectedCodes, 'ProcCode').join(',') : '',
          FilterAmountRemainingOver: this.amountRemainingOver === 0 ? null : this.amountRemainingOver,
          listPatients: this.selectedPatients
        };
      } else {
        // There's an existing list to which the user is adding patients
        if(this.selectedPatients.length === 0) {
          this.$toast.error('You do not have any patients selected. Please close this window and select patients first');          
          return false;  
        }
        payload = {
          ListID: this.selectedList.ListID,
          TaskTypeID: 4, // UNSCHEDULED TREATMENT
          SiteID: this.siteID,
          FilterDates: this.dateFrom && this.dateTo ? 'From ' + moment(this.dateFrom).format('MM-DD-YYYY') + ' to ' + moment(this.dateTo).format('MM-DD-YYYY') : '',
          FilterProvider: this.selectedProvider.length > 0 ? `${this.selectedProvider.FName} ${this.selectedProvider.LName}` : '',
          FilterCarriers: this.selectedPlans.length > 0 ?_.map(this.selectedPlans, 'CarrierName').join(',') : '',
          listPatients: this.selectedPatients,
        };
      }

      // console.log('payload:' + JSON.stringify(payload));

      try {
        await this.createList(payload);
        this.$bvModal.hide('create-list');
        //location.reload();
        if(!this.errored) {
          this.$router.go({
            path: this.$router.path,
            query: {
              t: + new Date()
            }
          });
        }
      } catch (err) {
          console.log(err);
      }
    },

    removeCode(option) {
      let removeIdx = this.selectedCodes.findIndex(each => {
        return each.ProcCode === option.ProcCode;
      });
      this.selectedCodes.splice(removeIdx, 1);
    },

    getProcCodesForCustomCategory(cat) {
      if(this.utCustomCodes) {
        let temp = this.utCustomCodes.filter((a) => a.categoryName === cat);
        if(temp.length > 0)
          return temp[0].procCodes;
        else
          return [];
      }
    },

    removeCustomTag(option) {
      console.log(`remove include custom tag called with option ${option}`);
      let procCodesToRemove = this.getProcCodesForCustomCategory(option);
      let orig = this.selectedCodes;
      for(let code of procCodesToRemove) {
        orig = orig.filter(item => item.ProcCode !== code.ProcCode && item.Descript !== code.Descript);
      }
      this.$set(this, 'selectedCodes', orig);

      let removeIdx = this.utSelectedCustomCategories.indexOf(option);
      this.utSelectedCustomCategories.splice(removeIdx,1);
    },

    removeProvider(option) {
      let removeIdx = this.selectedProvider.findIndex(each => {
        return each.FName === option.FName;
      });
      this.selectedProvider.splice(removeIdx, 1);
    },

    removePlan(option) {
      let removeIdx = this.selectedPlans.findIndex(each => {
        return each.CarrierNum === option.CarrierNum;
      });
      this.selectedPlans.splice(removeIdx, 1);
    },

    hideModal(ref) {
      this.$refs[ref].hide();
    },

    /*
    initCharts() {
      // eslint-disable-line
      $("#easy-pie1").easyPieChart({
        barColor: "#8fe5d4",
        trackColor: "#f8f9fa",
        scaleColor: false,
        lineWidth: 10,
        size: 120
      });
    },
    */

    chooseNone() {
      this.selectedPatients = [];
    },

    handleMainCheckbox() {
      this.isMainChecked ? this.chooseAll(this.unscheduledTreatmentData, this.selectedPatients) : this.chooseNone(this.selectedPatients);
    },

    // Patient selection methods (used by AdminTable) - match OverdueRecall implementation
    updateSelectedPatients(selectedPatients, patient) {
      const index = selectedPatients.findIndex(p => p.PatNum === patient.PatNum);
      if (index > -1) {
        selectedPatients.splice(index, 1);
      } else {
        selectedPatients.push(patient);
      }
    },

    isSelectedPatient(selectedPatients, patNum) {
      return selectedPatients.some(p => p.PatNum === patNum);
    },

    chooseAll(data, selectedPatients) {
      // Clear existing selection first
      selectedPatients.splice(0, selectedPatients.length);
      // Add all patients
      data.forEach(patient => {
        selectedPatients.push(patient);
      });
    },

    chooseNone(selectedPatients) {
      selectedPatients.splice(0, selectedPatients.length);
    },

    codeWithDesc({ ProcCode, Descript }){
      return `${ProcCode} — [${Descript}]`;
    },

    providerCustomLabel({FName, MI, LName, Suffix, Abbr}) {
      return `${FName} ${MI} ${LName} ${Suffix} (${Abbr})`;
    },

    planOptionsWithCount({CarrierName, totalplans}) {
      return `${CarrierName} (${totalplans})`;
    },

    employeesCustomLabel({Name}) {
      return `${Name}`;
    },

    successCountDownChanged(count) {
        this.setDismissSuccessCountDown(count);
    },

    callGoalsUpdated() {
      //console.log(`callGoalsUpdated called`);
      Vue.set(this.dataToDisplay[5][0], 'dailyAttempts', this.dataToDisplay[5][0].dailyAttempts + 1);
      Vue.set(this.dataToDisplay[6][0], 'weeklyAttempts', this.dataToDisplay[6][0].weeklyAttempts + 1);
      Vue.set(this.dataToDisplay[7][0], 'monthlyAttempts', this.dataToDisplay[7][0].monthlyAttempts + 1);
    },


    clearSelection() {
      this.selectedPatients = [];
    }
    
  },

  computed: {
    ...mapState({
        _lists: state => state.lists.lists,
        _masterList: state => state.lists.masterList,
    }),

    // ---- Follow-ups redesign (new branch only) -------------------------------------------------

    /**
     * Which template branch renders. An office user → STAGE.unscheduledTreatment and their own choice (the old page at "preview"). A Super Admin → the new page unless "Classic view" was picked.
     */
    fuUseNewUi() {
      // eslint-disable-next-line no-unused-expressions
      this.fuUiVersion; // re-read storage after the Classic / New view link
      return shouldUseNewFollowupsUi('unscheduledTreatment', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    // The tab bar: the header's permission rule; a count only for this page, whose rows are loaded.
    fuTabs() {
      const counts = {};
      if (this.unscheduledTreatmentData && this.fuCounts) counts.unscheduledTreatment = this.fuCounts.now;
      return visibleTabs((a, s) => !!(this.$can && this.$can(a, s)), counts);
    },

    // The same numbers the old Call Goals panel shows: attempts / the goal setting.
    fuGoals() {
      if (!this.allSettings) return {};
      const goal = k => (this.allSettings[k] ? this.allSettings[k].PropertyValue : undefined);
      return {
        daily: { done: this.dailyAttempts, goal: goal('UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL') },
        weekly: { done: this.weeklyAttempts, goal: goal('UNSCHEDULED_TREATMENT_WEEKLY_CALLS_GOAL') },
        monthly: { done: this.monthlyAttempts, goal: goal('UNSCHEDULED_TREATMENT_MONTHLY_CALLS_GOAL') },
      };
    },

    fuToday() {
      return moment().format('YYYY-MM-DD');
    },

    fuDatePopover() {
      return {
        kind: 'date',
        title: 'Tx plan date',
        from: this.fuYmd(this.dateFrom),
        to: this.fuYmd(this.dateTo),
        today: this.fuToday,
        width: 330,
        estHeight: 300,
      };
    },

    fuDateLabel() {
      const from = this.fuYmd(this.dateFrom);
      const to = this.fuYmd(this.dateTo);
      if (!from || !to) return 'Tx planned';
      const fmt = this.dateFormatStr || 'MM/DD/YYYY';
      return `Tx planned ${moment(from).format(fmt).slice(0, 5)} – ${moment(to).format(fmt)}`;
    },

    // The provider list the old multiselect offers, plus any selected provider it lacks.
    fuProviderPool() {
      return this.fuPool(this.getProviderOptions, Array.isArray(this.selectedProvider) ? this.selectedProvider : [], 'ProvNum');
    },

    fuSelectedProvNums() {
      return (Array.isArray(this.selectedProvider) ? this.selectedProvider : []).map(p => p.ProvNum);
    },

    fuProvidersPopover() {
      return {
        kind: 'providers',
        title: 'Providers',
        options: this.fuProviderPool.map(p => ({ value: p.ProvNum, label: this.fuProviderLabel(p) })),
        selected: this.fuSelectedProvNums,
        emptyText: 'No providers to pick from yet.',
        minPicksText: 'Pick at least one provider.',
        width: 320,
        estHeight: 300,
      };
    },

    fuProvidersLabel() {
      const n = this.fuSelectedProvNums.length;
      if (!n) return 'Providers';
      if (n === 1) {
        const p = this.fuProviderPool.find(x => x.ProvNum === this.fuSelectedProvNums[0]);
        return p ? this.fuProviderLabel(p) : '1 provider';
      }
      return n === this.fuProviderPool.length ? 'All providers' : `${n} providers`;
    },

    // The office's custom code categories (Settings → the old "Custom Tags"), unreadable ⇒ none.
    fuCustomCategories() {
      const s = this.allSettings && this.allSettings.UNSCHEDULED_TREATMENT_CUSTOM_CODES;
      if (!s || !s.PropertyValue) return [];
      try {
        const v = JSON.parse(s.PropertyValue);
        return Array.isArray(v) ? v.filter(c => c && c.categoryName && Array.isArray(c.procCodes)) : [];
      } catch (e) {
        return [];
      }
    },

    // The codes saved in Settings for each mode (the old "Load saved Proc Codes" icon).
    fuSavedCodes() {
      const read = (key) => {
        const s = this.allSettings && this.allSettings[key];
        if (!s || !s.PropertyValue) return [];
        try {
          const v = JSON.parse(s.PropertyValue);
          return Array.isArray(v) ? v.filter(c => c && c.ProcCode) : [];
        } catch (e) {
          return [];
        }
      };
      return { INCLUDE: read('UNSCHEDULED_TREATMENT_INCLUDE_CODES'), EXCLUDE: read('UNSCHEDULED_TREATMENT_EXCLUDE_CODES') };
    },

    // Every code More filters can show: the old multiselect's list (every code in Open Dental),
    // then any picked, custom-category or saved code that list lacks — so none is ever dropped.
    fuCodePool() {
      const extra = (this.selectedCodes || [])
        .concat(...this.fuCustomCategories.map(c => c.procCodes))
        .concat(this.fuSavedCodes.INCLUDE, this.fuSavedCodes.EXCLUDE);
      return this.fuPool(this.getCodeOptions, extra, 'ProcCode');
    },

    fuPlanPool() {
      return this.fuPool(this.getPlanOptions, this.selectedPlans, 'CarrierNum');
    },

    fuPriorityPool() {
      return this.fuPool(this.getTxPriorityOptions, this.selectedTxPriority, 'DefNum');
    },

    fuMoreSections() {
      const modes = [{ value: 'INCLUDE', label: 'Include' }, { value: 'EXCLUDE', label: 'Exclude' }];
      const codesOf = list => (list || []).map(c => c && c.ProcCode).filter(Boolean).map(String);
      const groups = this.fuCustomCategories.map(c => ({ key: `cat-${c.categoryName}`, label: c.categoryName, values: codesOf(c.procCodes) }));
      ['INCLUDE', 'EXCLUDE'].forEach((mode) => {
        const values = codesOf(this.fuSavedCodes[mode]);
        if (values.length) groups.push({ key: `saved-${mode}`, label: 'Saved codes', mode, values });
      });
      return [
        {
          key: 'codes',
          title: 'Procedure codes',
          hint: 'Type a code that isn’t listed to add it. D27* ticks every code starting with D27.',
          modes,
          options: this.fuCodePool.map(c => ({ value: String(c.ProcCode), label: this.fuCodeLabel(c) })),
          groups,
          searchable: true,
          searchPlaceholder: 'Search code or description…',
          creatable: { pattern: FU_TYPED_CODE, uppercase: true },
          wildcard: true,
          renderLimit: 200,
          emptyText: 'No procedure codes to pick from yet.',
        },
        {
          key: 'plans',
          title: 'Insurance plans',
          modes,
          options: this.fuPlanPool.map(p => ({ value: p.CarrierNum, label: p.CarrierName, count: p.totalplans })),
          searchable: true,
          emptyText: 'No insurance plans to pick from yet.',
        },
        {
          key: 'priorities',
          title: 'Treatment priorities',
          modes,
          options: this.fuPriorityPool.map(p => ({ value: p.DefNum, label: p.ItemName })),
          searchable: true,
          emptyText: 'No treatment priorities to pick from yet.',
        },
        {
          key: 'amount',
          title: 'Amount remaining over',
          hint: 'Insurance left this year: the annual maximum minus what has been used.',
          input: { prefix: '$', placeholder: 'Any', min: 0, errorText: 'Enter a whole dollar amount.' },
        },
      ];
    },

    fuMoreValue() {
      return {
        codes: { mode: this.includeOrExclude || 'INCLUDE', selected: (this.selectedCodes || []).map(c => String(c.ProcCode)) },
        plans: { mode: this.includeOrExcludePlan || 'INCLUDE', selected: (this.selectedPlans || []).map(p => p.CarrierNum) },
        priorities: { mode: this.includeOrExcludePriority || 'INCLUDE', selected: (this.selectedTxPriority || []).map(p => p.DefNum) },
        amount: { text: this.amountRemainingOver ? String(this.amountRemainingOver) : '' },
      };
    },

    // init() sends `amount` whenever amountRemainingOver is truthy — so it counts by the same test.
    fuMoreCount() {
      return (this.selectedCodes && this.selectedCodes.length ? 1 : 0)
        + (this.selectedPlans && this.selectedPlans.length ? 1 : 0)
        + (this.selectedTxPriority && this.selectedTxPriority.length ? 1 : 0)
        + (this.amountRemainingOver ? 1 : 0);
    },

    fuSortedLists() {
      return sortListsNewestFirst(this.getLists || []);
    },

    fuListsPopover() {
      const fmt = this.dateFormatStr || 'MM/DD/YYYY';
      return {
        kind: 'lists',
        title: 'Unscheduled treatment lists',
        width: 340,
        estHeight: 380,
        align: 'right',
        emptyText: 'No lists yet.',
        lists: this.fuSortedLists.map(l => ({
          id: l.ListID,
          name: l.Name,
          meta: [l.CreatedAt ? `Created ${moment(l.CreatedAt).format(fmt)}` : '', l.EmployeeName || ''].filter(Boolean).join(' · '),
          current: false,
        })),
      };
    },

    fuDialogLists() {
      return this.fuSortedLists.map(l => ({ id: l.ListID, name: l.Name }));
    },

    // The selection as the dialog shows it; `onList` names the list a patient is already on.
    fuDialogPatients() {
      const lists = this.getLists || [];
      return this.selectedPatients.map((p) => {
        let onList = null;
        if (p.BelongsToListID) {
          const l = lists.find(x => x.ListID === p.BelongsToListID);
          onList = (l && l.Name) || p.ListName || 'another list';
        }
        return { id: p.PatNum, name: p.Patient, onList };
      });
    },

    dataToDisplay() {
      if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
    },

    unscheduledTreatmentData() {
      if(this.dataToDisplay) {
        if(this.withAptsInNext60)
          return this.dataToDisplay[0].filter(a => a.NextVisit === null);
        else
          return this.dataToDisplay[0];
      }
    },


    /* Looks at selectedPatients list and if any of them belongs to a list then it will complain */
    somePatientsBelongsToList() {
      if (this.selectedPatients.some(p => p.BelongsToListID && p.BelongsToListID!== null)) {
        return true;
      } else {
        return false;
      }
    },

    getCodeOptions() {
      if(this.dataToDisplay)
        return this.dataToDisplay[1];
      else
        return [];
    },

    getProviderOptions() {
      if(this.dataToDisplay)
        return this.dataToDisplay[2];
      else
        return [];
    },

    getDefaultProvider() {
      if (this.providers && this.allSettings && this.allSettings.UNSCHEDULED_TREATMENT_DEFAULT_PROVIDER) {
        const provNums = String(this.allSettings.UNSCHEDULED_TREATMENT_DEFAULT_PROVIDER.PropertyValue)
          .split('|').map(n => parseInt(n, 10)).filter(n => !isNaN(n) && n > 0);
        const matches = _.uniqBy(this.providers.filter(p => provNums.includes(p.ProvNum)), 'ProvNum');
        if (matches.length > 0) return matches;
      }
      return [];
    },

    getPlanOptions() {
      if(this.dataToDisplay)
        return this.dataToDisplay[3];
      else
        return [];
    },

    getTxPriorityOptions() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[4];
      else
        return [];
    },

    getLists() {
      /*
      Comment by Nilay: initially we were filtering by whoever this list was assigned to. But we dont want that,
      All lists should be able to be worked on by anyone as long as they're assigned as accountable person for Overdue recall.
      As of now, our list creation process simply groups them into "buckets" like "tina's patients", "dr.k's patients", etc.
      Anyone can work on them, as long as they're assigned to do that type of task!
      */
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[5];
    },

    recentLists() {
      if(this.getLists && this.getLists.length > 0) {
        // Sort by CreatedAt descending and take the 3 most recent
        return [...this.getLists]
          .sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt))
          .slice(0, 3);
      }
      return [];
    },

    dailyAttempts() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[6][0].dailyAttempts;
    },

    dailyPercent() {
      if(this.dailyAttempts)
        return Math.round(this.dailyAttempts / this.allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue*100);
    },

    dailyStyle() {
      if(this.dailyPercent)
        return `width: ${this.dailyPercent}%;`;
    },

    weeklyAttempts() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[7][0].weeklyAttempts;
    },

    weeklyPercent() {
      if(this.weeklyAttempts)
        return Math.round(this.weeklyAttempts / this.allSettings.UNSCHEDULED_TREATMENT_WEEKLY_CALLS_GOAL.PropertyValue*100);
    },

    weeklyStyle() {
      if(this.weeklyPercent)
        return `width: ${this.weeklyPercent}%;`;
    },

    monthlyAttempts() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[8][0].monthlyAttempts;
    },

    monthlyPercent() {
      if(this.monthlyAttempts)
        return Math.round(this.monthlyAttempts / this.allSettings.UNSCHEDULED_TREATMENT_MONTHLY_CALLS_GOAL.PropertyValue*100);
    },

    monthlyStyle() {
      if(this.monthlyPercent)
        return `width: ${this.monthlyPercent}%;`;
    },    

    utCustomCodes() {
      if(this.allSettings && this.allSettings.UNSCHEDULED_TREATMENT_CUSTOM_CODES) {
        return JSON.parse(this.allSettings.UNSCHEDULED_TREATMENT_CUSTOM_CODES.PropertyValue);
      }
    },

    utCustomCategories() {
      if(this.utCustomCodes) {
        return _.map(this.utCustomCodes, 'categoryName');
      }
    },

  },

  watch: {
      getDefaultProvider(newValue, oldValue) {
        if (!oldValue || oldValue.length === 0) {
          this.init();
        }
      },

      utSelectedCustomCategories(newVal, oldVal) {
        //console.log('utSelectedCustomCategories changed', newVal, oldVal);
        let orig = this.selectedCodes;
        for(let cat of newVal) {
          //console.log('now getting proc codes for cat', cat);
          let procCodes = this.getProcCodesForCustomCategory(cat);
          //console.log(procCodes);        
          for(let code of procCodes) {
            let foundObj = this.selectedCodes.find(obj => obj.ProcCode === code.ProcCode && obj.Descript === code.Descript);        
            if(!foundObj)
              orig.push(code);
          }
        }
        this.$set(this, 'selectedCodes', orig);
      }
  }
};
</script>

<style src="./UnscheduledTreatment.scss" lang="scss" scoped/>
<!-- The redesign's page-level styles (new branch only) live with the Follow-ups pages. -->
<style src="../Followups/unscheduled-treatment-page.scss" lang="scss" scoped />
<style >
/*
.VuePagination{
  flex-direction: row-reverse;
  float: right;
  margin: 0 !important;
  padding: 0 !important;
  }

  .custom-radio .custom-control-label span{
    font-size: 12px;
  }

  .VueTables__limit{
    display: none !important;
  }
  .VueTables--client > .row{
    margin: 0 !important;
  }

  .unscheduled-treatment-table .table th span {
    font-size: 10px;
    font-weight: 600;
  }
  .abc-checkbox > div{
  display: block !important;
  }
  .unscheduled-treatment-table .table th span.VueTables__heading {
    font-size: 12px;
    border-top: 1px solid #d6dee5 !important;
    font-weight: bold;
  }

  .unscheduled-treatment-table .table th {
    border-top: 1px solid #d6dee5 !important;
  }
  

  .unscheduled-treatment-table .table td{
    padding: 15px 12px !important;
    font-size: 12px;
    vertical-align: middle;
  }
  .patient-name{
    color: #222;
    text-decoration: none;
  }
  .patient-name:hover {
    color: #222;
    text-decoration: none;
  }


*/

</style>

